import { type RefObject, useEffect } from "react";
import { useFocusTrap } from "./useFocusTrap.ts";

/**
 * Full a11y chrome for a hand-rolled modal/dialog, matching the shared Modal
 * primitive: a focus trap (Tab stays inside, focus restored to the opener on
 * close), Escape-to-close, and body scroll-lock while open.
 *
 * Put `ref` on the dialog PANEL element (the inner content box, not the
 * full-screen backdrop) and give that panel `role="dialog"` + `aria-modal`.
 * Modals that render only while open can omit `open` (defaults to true).
 */
export function useDialog(
	ref: RefObject<HTMLElement | null>,
	onClose: () => void,
	open = true,
): void {
	useFocusTrap(ref, open);
	useEscToClose(onClose, open);

	useEffect(() => {
		if (!open) return;
		const prevOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = prevOverflow;
		};
	}, [open]);
}

/**
 * Escape-to-close only (no focus trap, no scroll lock, no ref). Zero markup
 * change — use for inline overlays where threading a panel ref is awkward.
 */
export function useEscToClose(onClose: () => void, open = true): void {
	useEffect(() => {
		if (!open) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
		};
		document.addEventListener("keydown", onKey);
		return () => document.removeEventListener("keydown", onKey);
	}, [open, onClose]);
}
